import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { LoaderCircle, ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { useAuth } from '../auth/AuthContext';
import { DEFAULT_POST_LOGIN_PATH, redirectToPostLoginTarget, readReturnUrlFromSearchParams, resolvePostLoginPath } from '../utils/postLoginRedirect';

export function AuthProgressPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  const returnUrl = useMemo(
    () => resolvePostLoginPath(readReturnUrlFromSearchParams(searchParams) || DEFAULT_POST_LOGIN_PATH),
    [searchParams],
  );

  useEffect(() => {
    let isMounted = true;

    if (returnUrl.startsWith('/api/sso/')) {
      window.location.replace(returnUrl);
      return () => {
        isMounted = false;
      };
    }

    const waitForSession = async () => {
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const user = await refresh();
        if (user) {
          return user;
        }

        await new Promise((resolve) => {
          window.setTimeout(resolve, 80 * (attempt + 1));
        });
      }

      return null;
    };

    waitForSession()
      .then((user) => {
        if (!isMounted) {
          return;
        }

        if (user) {
          redirectToPostLoginTarget(returnUrl, navigate);
          return;
        }

        navigate(`/login?authState=signin-failed&returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true });
      })
      .catch(() => {
        if (isMounted) {
          navigate(`/login?authState=signin-failed&returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [navigate, refresh, returnUrl]);

  return (
    <div className="login-screen">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="login-card"
      >
        <BrandMark />
        <div className="login-title-row">
          <div>
            <h2>Completing sign-in</h2>
            <p className="hint-text">We are validating your Kinde session and applying access rules.</p>
          </div>
          <div className="icon-chip">
            <ShieldCheck size={18} />
          </div>
        </div>
        <div className="progress-panel">
          <LoaderCircle size={20} className="spin" />
          <span>Finalizing authentication...</span>
        </div>
      </motion.div>
    </div>
  );
}
